import { AppContext } from '@prisjakt/ui-app-context';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { IntersectionOptions } from 'react-intersection-observer';
import { useLocation } from 'react-router';
import { withAds } from 'src/components/ads/context/withAds';
import { AdServiceContextT } from 'src/components/page/page-content';
import SearchBar from 'src/components/search-bar';
import { useSearchBarContext } from 'src/components/search-bar/search-bar-context';
import { ViewportContext } from 'src/components/viewport';
import { Hero, SearchWrapper, Content, HeroTitle } from './style';

type Props = {
  active?: boolean;
  ads?: any;
  adServiceContext: AdServiceContextT;
};

let ref: Element;

const SearchHeroComponent = ({ active = true, adServiceContext }: Props) => {
  const location = useLocation();
  const { setFocused } = useSearchBarContext();
  const searchBarRef = useRef(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(true);
  const { isMobile } = useContext(ViewportContext);
  const { market, t } = useContext(AppContext);
  const isFirstPage = location.pathname === '/';
  const hasTakeoverAd = Boolean(adServiceContext.takeover);

  useEffect(() => {
    setFocused(!isMobile && isFirstPage);
  }, [isMobile]);

  const checkVisible = useCallback((entries: IntersectionObserverEntry[]) => {
    const sendIsHeroVisibleEvent = (visible: boolean) => {
      const event = new CustomEvent('is-hero-visible-event', {
        detail: { visible },
      });
      dispatchEvent(event);
    };

    const [entry] = entries;

    if (entry.intersectionRatio < 0.9) {
      setIsVisible(false);
      sendIsHeroVisibleEvent(false);
      return;
    }

    setIsVisible(true);
    sendIsHeroVisibleEvent(true);
  }, []);

  useEffect(() => {
    const options: IntersectionOptions = {
      threshold: 0.9,
    };

    const observer = new IntersectionObserver(checkVisible, options);

    if (searchBarRef.current) {
      ref = searchBarRef.current;
    }

    observer.observe(ref);

    return () => {
      observer.unobserve(ref);
    };
  }, [checkVisible, searchBarRef]);

  const backgroundImage = `${market.cdn}/g/rfe/icons/hero-things-cat-baby_desktop_1960x500.png`;

  return (
    <Hero
      $active={active}
      $backgroundImage={backgroundImage}
      $fullWidth={hasTakeoverAd}
      data-test="hero-search"
      ref={heroRef}
    >
      <Content ref={searchBarRef}>
        <HeroTitle>{t('landing', 'hero.title')}</HeroTitle>
        <SearchWrapper>
          <SearchBar visible={isVisible} />
        </SearchWrapper>
      </Content>
    </Hero>
  );
};

export const SearchHero = withAds(SearchHeroComponent);
